import { memo, useCallback, useMemo, useState } from 'react';
import { Button, Filters, SearchInput } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Download } from 'lucide-react';

import {
  ACCESS_FILTER,
  CHECK_FILTER,
  MISSING_FILTER,
  REVIEW_STATUS_FILTER,
} from '../../../constants/reviewFilters.js';
import StatusChip from './StatusChip.jsx';

const STATUS_ORDER = Object.values(REVIEW_STATUS_FILTER);
const KEYS = {
  [ACCESS_FILTER.NONE]: 'none',
  [ACCESS_FILTER.ASSIGNED]: 'assigned',
  [ACCESS_FILTER.ADMIN]: 'admin',
  [ACCESS_FILTER.NO_STUDIO]: 'noStudio',
  [MISSING_FILTER.ROLE]: 'role',
  [MISSING_FILTER.JOB_TITLE]: 'jobTitle',
  [MISSING_FILTER.LOCATION]: 'location',
  [MISSING_FILTER.MANAGER]: 'manager',
  [CHECK_FILTER.OUTSIDE_DOMAIN]: 'outsideDomain',
  [CHECK_FILTER.POSSIBLE_DUPLICATE]: 'possibleDuplicate',
};

/**
 * Search (all columns, committed on Enter), the four dropdown filters, the status counter
 * chips and the errors download.
 *
 * @param {object} props
 * @param {string} props.search Committed search text.
 * @param {{ access: string[], missing: string[], check: string[], fileRole: string[], status: string }} props.filters
 * @param {string[]} props.fileRoles Role names found in the file (options of "Role in your file").
 * @param {{ all: number, new: number, update: number, error: number }} props.counters
 * @param {(query: string) => void} props.onSearch
 * @param {(change: object) => void} props.onFilters
 * @param {() => void} props.onClearAll
 * @param {boolean} props.hasErrors Some row is in error: otherwise there is nothing to download.
 * @param {() => void} props.onDownloadErrors
 */
function ReviewToolbar({ search, filters, fileRoles, counters, hasErrors, onSearch, onFilters, onClearAll, onDownloadErrors }) {
  const { t } = useTranslation('people-access');
  const [draft, setDraft] = useState(search);
  const [syncedSearch, setSyncedSearch] = useState(search);
  // The committed search can change from outside (Clear filters): follow it.
  if (search !== syncedSearch) {
    setSyncedSearch(search);
    setDraft(search);
  }

  const handleChange = useCallback((event) => setDraft(event.target.value), []);
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter') onSearch(draft);
    },
    [onSearch, draft],
  );
  const handleClear = useCallback(() => {
    setDraft('');
    onSearch('');
  }, [onSearch]);

  const option = useCallback(
    (group, value) => ({ value, label: t(`import.review.filters.${group}.${KEYS[value]}`) }),
    [t],
  );
  const slots = useMemo(
    () => [
      {
        id: 'access',
        label: t('import.review.filters.access.label'),
        multiple: false,
        value: filters.access,
        onChange: (next) => onFilters({ access: next }),
        options: Object.values(ACCESS_FILTER).map((v) => option('access', v)),
      },
      {
        id: 'missing',
        label: t('import.review.filters.missing.label'),
        multiple: true,
        value: filters.missing,
        onChange: (next) => onFilters({ missing: next }),
        options: Object.values(MISSING_FILTER).map((v) => option('missing', v)),
      },
      {
        id: 'check',
        label: t('import.review.filters.check.label'),
        multiple: false,
        value: filters.check,
        onChange: (next) => onFilters({ check: next }),
        options: Object.values(CHECK_FILTER).map((v) => option('check', v)),
      },
      {
        id: 'fileRole',
        label: t('import.review.filters.fileRole.label'),
        multiple: false,
        value: filters.fileRole,
        onChange: (next) => onFilters({ fileRole: next }),
        options: fileRoles.map((name) => ({ value: name, label: name })),
      },
    ],
    [t, filters, onFilters, option, fileRoles],
  );
  const i18n = useMemo(
    () => ({
      clearFilters: t('import.review.clearFilters'),
      removeFilter: (label) => t('import.review.filtersI18n.removeFilter', { label }),
      addedAnnounce: (label) => t('import.review.filtersI18n.addedAnnounce', { label }),
      removedAnnounce: (label) => t('import.review.filtersI18n.removedAnnounce', { label }),
      clearedAllAnnounce: t('import.review.filtersI18n.clearedAll'),
      moreChip: (count) => t('import.review.filtersI18n.moreChip', { count }),
    }),
    [t],
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={draft}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onClear={handleClear}
          placeholder={t('import.review.searchPlaceholder')}
          aria-label={t('import.review.searchLabel')}
          className="w-52"
        />
        <Filters
          filters={slots}
          maxFilters={4}
          showAllFilters={false}
          onClearAll={onClearAll}
          i18n={i18n}
        />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div role="group" aria-label={t('import.review.counters.label')} className="flex flex-wrap items-center gap-2">
          {STATUS_ORDER.map((status) => (
            <StatusChip
              key={status}
              status={status}
              selected={filters.status === status}
              label={t(`import.review.counters.${status}`, { count: counters[status] })}
              onSelect={onFilters}
            />
          ))}
        </div>
        <Button variant="secondary" size="sm" leftIcon={Download} disabled={!hasErrors} onClick={onDownloadErrors}>
          {t('import.review.downloadErrors')}
        </Button>
      </div>
    </div>
  );
}

export default memo(ReviewToolbar);
